/**
 * Helper function that recursively cleans an object by removing null and undefined values from arrays and objects.
 * It also removes keys with empty objects.
 *
 * @param {any} value - The value to clean.
 * @return {any} The cleaned value.
 */
const clean = (value: any): any => {
  // If the value is an array, map over each item and recursively clean it.
  // Then filter out any undefined items.
  if (Array.isArray(value)) {
    return value
      .map(clean) // Recursive call to clean each item in the array.
      .filter((item) => item !== undefined); // Remove undefined items from the array.
  }

  // If the value is an object (not null), map over each property and recursively clean its value.
  // Then create a new object with only the properties whose values are not undefined or empty objects.
  if (value !== null && typeof value === 'object') {
    return Object.keys(value).reduce<Record<string, any>>(
      (accumulator, key) => {
        const cleanedValue = clean(value[key]); // Recursive call to clean each property value.

        if (
          cleanedValue !== undefined &&
          !(
            cleanedValue !== null &&
            typeof cleanedValue === 'object' &&
            Object.keys(cleanedValue).length === 0
          )
        ) {
          accumulator[key] = cleanedValue; // Add the property to the accumulator only if its value is not undefined or an empty object.
        }

        return accumulator;
      },
      {},
    );
  }

  // If the value is neither an array nor an object, return it as is.
  return value;
};

/**
 * Removes keys with undefined values and empty objects from an object.
 * Can be used to clean up data before sending it to the API.
 *
 * @param {any} obj - The object to remove undefined keys from.
 * @return {any} The object with undefined keys removed.
 */
export const removeUndefinedKeys = (object: any): any => {
  if (object == null) {
    return object;
  }

  return clean(object);
};
